/**
 * Resolve the "N steps" badge count for a Flow Designer node.
 *
 * Mirrors `resolveCachePlaybookNodes`' precedence: the cache-row playbook wins
 * whenever it actually has steps, otherwise the node's configJson playbook is
 * used. This matters under playbook-v3, which FREEZES `config_json` rather than
 * clearing it — a node authored pre-v3 and re-cached under v3 keeps a stale
 * `config.playbookSteps` array, so a naive `Array.isArray(config) ? config.length
 * : row` check would render the stale config length while the overlay renders the
 * fresh row playbook. Preferring the row count when it is > 0 keeps the badge and
 * the overlay consistent, while a legacy/v2 row with no `playbook_json`
 * (`cachePlaybookStepCount` 0) correctly falls back to the configJson length.
 *
 * Returns `undefined` (not 0) when there is no playbook to count, so the badge
 * shows a bare "Cached" state rather than "0 steps".
 *
 * @module FlowDesigner/utils/resolvePlaybookStepCount
 */

export function resolvePlaybookStepCount(args: {
  /** The node's configJson playbook steps (may be absent, empty, or stale). */
  configPlaybookSteps: unknown;
  /** Length of the cache row's `playbook_json` from the API (null when uncached). */
  cachePlaybookStepCount: number | null | undefined;
}): number | undefined {
  const rowLen =
    typeof args.cachePlaybookStepCount === 'number' ? args.cachePlaybookStepCount : 0;
  const configLen = Array.isArray(args.configPlaybookSteps)
    ? args.configPlaybookSteps.length
    : 0;
  const effective = rowLen > 0 ? rowLen : configLen;
  return effective > 0 ? effective : undefined;
}
